import { useTranslation } from '@aimsos/i18n';
import { Button, IconHighlight, ModalLayout } from '@aimsos/ui';

import { useModalEscape } from '../hooks/useModalEscape.js';
import { ModalFooter } from './parts';

import { TriangleAlert } from 'lucide-react';

/**
 * Warning shown before a change wipes the configuration under it — a source
 * switch (CT-5191) or an operation switch (CT-5194), both through DS-3.
 *
 * It lists **what** will be cleared rather than saying "your changes will be
 * lost": the user configured filters, an operation and columns separately, and
 * needs to know which of them is about to go. The two kinds clear different
 * sets — an operation switch leaves the filters alone, because they narrow the
 * source, not the operation.
 */
function ResetDownstreamConfigModal({ pendingChange, onCancel, onConfirm }) {
  const { t } = useTranslation('dashboards');

  useModalEscape(Boolean(pendingChange), onCancel);
  if (!pendingChange) return null;

  return (
    <ModalLayout
      isOpen
      onClose={onCancel}
      useBodyPortal
      maxWidthClassName="max-w-md"
      titlePrefix={
        <IconHighlight icon={TriangleAlert} bgColor="Yellow" iconColor="Yellow" size="medium" />
      }
      title={t(`widgetBuilder.data.reset.${pendingChange.kind}.title`)}
    >
      <div className="flex flex-col gap-3 px-6 py-4">
        <p className="m-0 text-sm text-content-secondary">
          {t(`widgetBuilder.data.reset.${pendingChange.kind}.body`)}
        </p>
        <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-xs text-content-secondary">
          {pendingChange.clears.map((key) => (
            <li key={key}>{t(`widgetBuilder.data.reset.clears.${key}`)}</li>
          ))}
        </ul>
      </div>

      <ModalFooter
        cancelLabel={t(`widgetBuilder.data.reset.${pendingChange.kind}.cancel`)}
        onCancel={onCancel}
        primaryAction={
          <Button variant="danger" onClick={onConfirm} className="!text-[12px]">
            {t(`widgetBuilder.data.reset.${pendingChange.kind}.confirm`)}
          </Button>
        }
      />
    </ModalLayout>
  );
}

export default ResetDownstreamConfigModal;
